// ─── DocumentoWorkflowBar — wrapper ultra-fino del shared (ADR-025 ext 0661 + 0661+ fix) ────
// Inyecta el adapter DOC al shared `<ArtefactoWorkflowBar>` para que ORGANIGRAMA,
// DOC y FLUJOGRAMA usen el MISMO componente UI (idéntica UX: pill Ed. + badge
// etapa + WorkflowBar + historial popover + nueva edición).
//
// Endpoints DOC (verificados via 404 trace + versiones.routes.ts:7-17 backend):
//   GET  /api/v1/versiones/documento/:codigo                  (listar versiones)
//   POST /api/v1/versiones/documento/:codigo/nueva-edicion    (crear borrador / nueva edición)
//   POST /api/v1/versiones/:id/transitar                      (motor unificado ADR-025)
//   GET  /api/v1/workflow/etapa-actual/DOC/:id                (etapa multi-etapa)
//
// FIX 0661+: el grill T-0 asumió rutas `/documentos/:codigo/versiones` que NO
// existen en backend. La ruta real está bajo `/versiones/documento/:codigo`
// (versionesRouter line 10 + 14). Detectado en audit Playwright (404 + console).

import { useMemo } from 'react';
import {
  ArtefactoWorkflowBar,
  type ArtefactoAdapter,
} from '../../../../shared/components/ArtefactoWorkflowBar';
import type { EstadoWorkflow } from '../../../../shared/components/WorkflowBar';

interface DocumentoWorkflowBarProps {
  /** Código del documento (ej: 'PE.04.IT.01'). Usado en endpoints. */
  codigo: string;
  /** Si false, oculta el componente. Default true. */
  enabled?: boolean;
  /** Callback con el estado de la versión activa cada vez que cambia. */
  onVersionStateChange?: (state: EstadoWorkflow | null) => void;
  /** Callback al click en una versión del popover historial. */
  onSelectVersion?: (versionId: string, numeroEdicion: number) => void;
}

function buildDocumentoAdapter(codigo: string): ArtefactoAdapter {
  return {
    artefactoTipo: 'DOC',
    endpointListVersiones: `/versiones/documento/${codigo}`,
    buildEndpointTransitar: (versionId) => `/versiones/${versionId}/transitar`,
    buildEndpointEtapaActual: (versionId) => `/workflow/etapa-actual/DOC/${versionId}`,
    endpointCrearBorrador: `/versiones/documento/${codigo}/nueva-edicion`,
    buildCrearBorradorPayload: (descripcion) => ({ descripcionCambios: descripcion }),
    tieneVersionado: true,
  };
}

export function DocumentoWorkflowBar({
  codigo,
  enabled = true,
  onVersionStateChange,
  onSelectVersion,
}: DocumentoWorkflowBarProps) {
  // Memoizar adapter para evitar bucle de recargas (code-reviewer 0661 HIGH-1):
  // sin useMemo, cada render del padre crea adapter nuevo → useEffect del hook
  // dispara cargarVersiones en cascada.
  const adapter = useMemo(() => buildDocumentoAdapter(codigo), [codigo]);
  return (
    <ArtefactoWorkflowBar
      adapter={adapter}
      enabled={enabled}
      onVersionStateChange={onVersionStateChange}
      onSelectVersion={onSelectVersion}
      compact
    />
  );
}
